import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import {
  DEFAULT_IMPORT_MAPPING,
  guessImportMapping,
  IMPORT_FIELD_GROUPS,
  mappingIsComplete,
} from '../api/importMapping';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';
import type { PortfolioImportMapping } from '@workspace/types';

export function ContactAgentImportPage() {
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [mapping, setMapping] = useState<PortfolioImportMapping>(DEFAULT_IMPORT_MAPPING);
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<Array<Record<string, string>>>([]);
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const mappingReady = useMemo(() => mappingIsComplete(mapping), [mapping]);

  useEffect(() => {
    if (!file || !ready) {
      setColumns([]);
      setSampleRows([]);
      setRowCount(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    setResult('');
    void contactAgentApi
      .previewImportPortfolio(file)
      .then(preview => {
        if (cancelled) {
          return;
        }
        setColumns(preview.columns);
        setSampleRows(preview.sampleRows);
        setRowCount(preview.rowCount);
        setMapping(guessImportMapping(preview.columns));
      })
      .catch(err => {
        if (!cancelled) {
          setResult(contactAgentError(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPreviewing(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [file, ready, tenantId]);

  const applyAutoMap = () => {
    if (columns.length === 0) {
      return;
    }
    setMapping(guessImportMapping(columns));
  };

  const handleImport = async () => {
    if (!file) {
      setResult('Selecciona un archivo CSV o XLSX.');
      return;
    }
    if (!ready) {
      setResult('Selecciona un tenant antes de importar.');
      return;
    }
    if (!mappingReady) {
      setResult('Completa el mapeo de teléfono, factura, monto y vencimiento.');
      return;
    }
    setLoading(true);
    try {
      const data = await contactAgentApi.importPortfolio(file, mapping);
      setResult(
        `Listo. ${data.createdCount} altas, ${data.updatedCount} actualizaciones, ${data.skippedCount} omitidas.`,
      );
    } catch (error) {
      setResult(contactAgentError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Importar cartera'
        description='Subí un Excel o CSV. Detectamos las columnas y te mostramos una vista previa antes de importar.'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader>
          <CardTitle>1. Archivo</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <label className='flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-10 text-center transition-colors hover:bg-muted/40'>
            <p className='font-medium'>{file ? file.name : 'Arrastrá el archivo o hacé clic'}</p>
            <p className='mt-1 text-sm text-muted-foreground'>CSV, XLSX o XLS · hasta 10.000 filas</p>
            <Input
              className='mt-4 max-w-xs'
              type='file'
              accept='.csv,.xlsx,.xls'
              onChange={event => setFile(event.target.files?.[0] ?? null)}
            />
          </label>
          {previewing ? <p className='text-sm text-muted-foreground'>Leyendo columnas...</p> : null}
          {rowCount !== null ? (
            <p className='text-sm text-muted-foreground'>
              {rowCount} fila(s) detectadas · {columns.length} columna(s)
            </p>
          ) : null}
        </CardContent>
      </Card>

      {columns.length > 0 ? (
        <Card className='rounded-2xl shadow-sm'>
          <CardHeader className='flex flex-row items-center justify-between gap-4'>
            <CardTitle>2. Mapeo de columnas</CardTitle>
            <Button type='button' variant='outline' size='sm' onClick={applyAutoMap}>
              Detectar automáticamente
            </Button>
          </CardHeader>
          <CardContent className='space-y-6'>
            {IMPORT_FIELD_GROUPS.map(group => (
              <div key={group.title} className='space-y-3'>
                <div className='flex items-center gap-2'>
                  <h3 className='text-sm font-semibold'>{group.title}</h3>
                  {group.title === 'Obligatorios' ? (
                    <Badge variant='secondary'>requeridos</Badge>
                  ) : null}
                </div>
                <div className='grid gap-4 md:grid-cols-2'>
                  {group.fields.map(field => (
                    <div key={field.key} className='space-y-2 rounded-xl border p-3'>
                      <Label htmlFor={field.key}>{field.label}</Label>
                      <select
                        id={field.key}
                        className='flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm'
                        value={String(mapping[field.key] ?? '')}
                        onChange={event =>
                          setMapping(current => ({
                            ...current,
                            [field.key]: event.target.value,
                          }))
                        }
                      >
                        <option value=''>— Sin asignar —</option>
                        {columns.map(column => (
                          <option key={column} value={column}>
                            {column}
                          </option>
                        ))}
                      </select>
                      <p className='text-xs text-muted-foreground'>{field.hint}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {sampleRows.length > 0 ? (
              <div className='space-y-2 overflow-x-auto'>
                <h3 className='text-sm font-semibold'>Vista previa (primeras filas)</h3>
                <table className='min-w-full text-left text-sm'>
                  <thead>
                    <tr className='border-b'>
                      {columns.map(column => (
                        <th key={column} className='px-2 py-2 font-medium'>
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sampleRows.map((row, index) => (
                      <tr key={index} className='border-b'>
                        {columns.map(column => (
                          <td key={column} className='px-2 py-2 text-muted-foreground'>
                            {String(row[column] ?? '').slice(0, 40)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className='flex items-center justify-between gap-4'>
        {result ? <p className='text-sm text-muted-foreground'>{result}</p> : <span />}
        <Button onClick={() => void handleImport()} disabled={loading || previewing || !ready || !file}>
          {loading ? 'Importando...' : 'Importar cartera'}
        </Button>
      </div>
    </ContactAgentPage>
  );
}
